import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface TranslationResource {
    prefix: string;
    suffix?: string;
    optional?: boolean;
}

/**
 * Ensures that multiple translation files can be loaded and used at the same time (e.g. client + shared).
 *
 * The whole implementation was graciously stolen from https://github.com/rbalet/ngx-translate-multi-http-loader
 * (MIT License)
 */
export class MultiTranslateHttpLoader implements TranslateLoader {
    constructor(
        private readonly _handler: HttpClient,
        private readonly _resourcesPrefix: (string | TranslationResource)[]
    ) {}

    public getTranslation(lang: string): Observable<any> {
        const requests: Observable<Object | {}>[] = this._resourcesPrefix.map(
            (resource) => {
                let path: string;

                if (typeof resource === 'string')
                    path = `${resource}${lang}.json`;
                else
                    path = `${resource.prefix}${lang}${resource.suffix ?? '.json'}`;

                return this._handler.get(path).pipe(
                    catchError((res) => {
                        if (
                            typeof resource !== 'string' &&
                            !resource.optional
                        ) {
                            console.group();
                            console.error(
                                'Something went wrong for the following translation file:',
                                path
                            );
                            console.error(res);
                            console.groupEnd();
                        }
                        return of({});
                    })
                );
            }
        );

        return forkJoin(requests).pipe(
            map((response) =>
                response.reduce((acc, curr) => this.mergeDeep(acc, curr), {})
            )
        );
    }

    isObject(item: any): boolean {
        return item && typeof item === 'object' && !Array.isArray(item);
    }

    mergeDeep(target: any, source: any): any {
        const output = { ...target };

        if (!this.isObject(target)) {
            return this.mergeDeep({}, source);
        }

        if (this.isObject(target) && this.isObject(source)) {
            Object.keys(source).forEach((key: any) => {
                if (this.isObject(source[key])) {
                    if (!(key in target)) {
                        Object.assign(output, { [key]: source[key] });
                    } else {
                        output[key] = this.mergeDeep(target[key], source[key]);
                    }
                } else {
                    Object.assign(output, { [key]: source[key] });
                }
            });
        }
        return output;
    }
}
